/**
 * useChainTermini.ts - Hook to identify chain endpoints for floating add buttons
 *
 * This hook analyzes the flow graph to find all terminal nodes (nodes with no
 * outgoing edges) and calculates positions for placing floating "+" buttons
 * below them.
 *
 * Related:
 *   - components/ChainTerminusButton.tsx - Renders the + button at each terminus
 *   - utils/layoutConstants.ts - Spacing constants
 */

import { useMemo } from 'react';
import type { Edge, Node } from '@xyflow/react';
import type { BaseNodeData } from '../types';
import { NODE_TYPES } from '../types';
import { hasEstimatedHeight } from '../types/typeGuards';
import {
	FLOW_VERTICAL_SPACING,
	NODE_WIDTH,
	SEGMENT_NODE,
	STEP_NODE_COMPACT_HEIGHT,
	STEP_NODE_EXPANDED_MIN_HEIGHT,
	TRIGGER_NODE,
} from '../utils/layoutConstants';
import { getNodeBranchNestingDepth } from '../utils/flowPathing';

/**
 * Information about a chain terminus where a + button should appear.
 */
export interface ChainTerminus {
	/** ID of the terminal node */
	nodeId: string;
	/** Position for the + button (centered below the node) */
	position: { x: number; y: number };
	/** Number of ancestor branch boundaries (segments / behavior cases) above this node */
	nestingDepth?: number;
	/** Information about the parent switch branch if this terminus is in a branch */
	branchInfo?: {
		parentSwitchId: string;
		caseIndex: number;
		caseName: string;
		/** The case statement ID — used to match against the API's cases array reliably */
		caseId?: string;
	};
	/** The actual terminal node for reference */
	node: Node<BaseNodeData>;
}

// If a compact node still reports a very large measured height, that measurement is
// usually stale from a prior expansion and should be ignored.
const MAX_COMPACT_NODE_HEIGHT = 200;

function getNodeWidthForTerminus(node: Node<BaseNodeData>): number {
	if (typeof node.measured?.width === 'number') {
		return node.measured.width;
	}

	if (typeof node.width === 'number') {
		return node.width;
	}

	if (node.type === 'segment') {
		return SEGMENT_NODE.WIDTH;
	}

	if (node.type === 'trigger') {
		return TRIGGER_NODE.WIDTH;
	}

	return NODE_WIDTH;
}

function getNodeHeightForTerminus(node: Node<BaseNodeData>): number {
	const measuredHeight = typeof node.measured?.height === 'number' ? node.measured.height : null;
	const estimatedHeight = hasEstimatedHeight(node.data) ? node.data.estimatedHeight : null;
	const isExpanded = (node.data as { isExpanded?: unknown } | undefined)?.isExpanded === true;

	if (isExpanded) {
		if (measuredHeight !== null && estimatedHeight !== null) {
			return Math.max(measuredHeight, estimatedHeight);
		}
		if (estimatedHeight !== null) {
			return estimatedHeight;
		}
		if (measuredHeight !== null) {
			return measuredHeight;
		}
	} else if (measuredHeight !== null && measuredHeight <= MAX_COMPACT_NODE_HEIGHT) {
		return measuredHeight;
	}

	if (typeof node.height === 'number') {
		return node.height;
	}

	if (estimatedHeight !== null) {
		return estimatedHeight;
	}

	if (node.type === 'segment') {
		return SEGMENT_NODE.MIN_HEIGHT;
	}

	if (node.type === 'trigger') {
		return TRIGGER_NODE.HEIGHT;
	}

	return isExpanded ? STEP_NODE_EXPANDED_MIN_HEIGHT : STEP_NODE_COMPACT_HEIGHT;
}

/**
 * Identifies terminal nodes for floating add buttons.
 *
 * Shows a + button at the end of every chain except:
 * - Error nodes (invalid state)
 * - Condition nodes (intermediate branching points)
 * - Phantom target nodes (drag targets)
 * - Start Automation steps (terminal steps that should not allow downstream steps)
 * - Hidden nodes (e.g., switch nodes whose edges originate from the visual parent)
 * - Switch step nodes (branches originate from segments, not the switch itself)
 *
 * Trigger nodes are usually skipped because they normally have a step after them,
 * but a lone trigger with no outgoing edge is a valid terminus. That allows the
 * initial post-trigger "Add Step" button to render for empty automations.
 *
 * Behavior case nodes ARE valid termini — they serve as branch entry points
 * where users can add steps to build out each behavior path.
 *
 * Trigger nodes are usually skipped because they normally have a step after them,
 * but a lone trigger with no outgoing edge is a valid terminus. That allows the
 * initial post-trigger "Add Step" button to render for empty automations.
 *
 * @param nodes - Array of React Flow nodes
 * @param edges - Array of React Flow edges
 * @returns Array of ChainTerminus objects for each valid terminus
 */
export function useChainTermini(nodes: Node<BaseNodeData>[], edges: Edge[]): ChainTerminus[] {
	return useMemo(() => {
		if (nodes.length === 0) {
			return [];
		}

		// Build a set of all source node IDs (nodes that have outgoing edges)
		const nodesWithOutgoingEdges = new Set<string>();
		for (const edge of edges) {
			nodesWithOutgoingEdges.add(edge.source);
		}

		// Find terminal nodes (nodes without outgoing edges)
		const termini: ChainTerminus[] = [];

		for (const node of nodes) {
			// Skip nodes that have outgoing edges
			if (nodesWithOutgoingEdges.has(node.id)) {
				continue;
			}

			// Skip error nodes
			if (node.type === 'error') {
				continue;
			}

			// Skip condition nodes (they're intermediate branching points)
			if (node.type === 'condition') {
				continue;
			}

			// Skip phantom target nodes
			if (node.type === 'phantomTarget') {
				continue;
			}

			// Skip terminal steps that intentionally end the chain.
			if (node.type === NODE_TYPES.TRIGGER_AUTOMATION_STEP) {
				continue;
			}

			// Skip hidden nodes (e.g., switch nodes whose edges originate from the visual parent)
			if (node.hidden) {
				continue;
			}

			// Skip switch step nodes — branches originate from segments, not the switch itself
			if (node.type === 'switchStep') {
				continue;
			}

			// Get node data for additional checks
			const nodeData = node.data;

			// Skip nodes that might be loading or not yet initialized
			if (!nodeData || !nodeData.label) {
				continue;
			}

			// Calculate position for the + button (centered below the node).
			// Height uses measured/estimated fallbacks so it stays attached to expanded cards
			// even before React Flow finishes reporting fresh measurements.
			const nodeWidth = getNodeWidthForTerminus(node);
			const nodeHeight = getNodeHeightForTerminus(node);
			const buttonX = (node.position?.x ?? 0) + nodeWidth / 2;
			const buttonY = (node.position?.y ?? 0) + nodeHeight + FLOW_VERTICAL_SPACING / 2;

			// Extract branch info if this node is part of a switch branch
			let branchInfo: ChainTerminus['branchInfo'];

			// Segment nodes carry authoritative branch metadata.
			if ('parentSwitchNodeId' in nodeData && 'segmentIndex' in nodeData) {
				const segmentData = nodeData as unknown as {
					parentSwitchNodeId: string;
					segmentIndex: number;
					caseId?: string;
					globalSegmentOrdinal?: number;
				};
				const segmentNumber = segmentData.globalSegmentOrdinal || segmentData.segmentIndex + 1;
				branchInfo = {
					parentSwitchId: segmentData.parentSwitchNodeId,
					caseIndex: segmentData.segmentIndex,
					caseName: `Segment #${segmentNumber}`,
					caseId: segmentData.caseId,
				};
			}

			// Behavior case nodes also carry authoritative branch metadata.
			if (!branchInfo && node.type === NODE_TYPES.BEHAVIOR_CASE && 'parentSwitchNodeId' in nodeData) {
				const behaviorCaseData = nodeData as unknown as {
					parentSwitchNodeId: string;
					caseIndex: number;
					caseName?: string;
					caseId?: string;
					globalSegmentOrdinal?: number;
				};
				const fallbackCaseName = behaviorCaseData.globalSegmentOrdinal
					? `Segment #${behaviorCaseData.globalSegmentOrdinal}`
					: `Case #${behaviorCaseData.caseIndex + 1}`;
				branchInfo = {
					parentSwitchId: behaviorCaseData.parentSwitchNodeId,
					caseIndex: behaviorCaseData.caseIndex,
					caseName: behaviorCaseData.caseName || fallbackCaseName,
					caseId: behaviorCaseData.caseId,
				};
			}

			if (!branchInfo && 'parentCaseInfo' in nodeData && nodeData.parentCaseInfo) {
				const parentCaseInfo = nodeData.parentCaseInfo as {
					caseIndex: number;
					caseName: string;
					caseId?: string;
					parentSwitchNodeId?: string;
				};
				const parentSwitchId =
					typeof parentCaseInfo.parentSwitchNodeId === 'string'
						? parentCaseInfo.parentSwitchNodeId
						: findParentSwitchId(nodes, edges, node.id);
				if (parentSwitchId) {
					branchInfo = {
						parentSwitchId,
						caseIndex: parentCaseInfo.caseIndex,
						caseName: parentCaseInfo.caseName,
						caseId: parentCaseInfo.caseId,
					};
				}
			}

			// For step nodes inside a branch that don't have parentCaseInfo (only the first
			// child in a branch gets it), trace backwards through edges to find the ancestor
			// segment node and derive branchInfo from it.
			if (!branchInfo) {
				const segmentInfo = findParentSegmentInfo(nodes, edges, node.id);
				if (segmentInfo) {
					branchInfo = segmentInfo;
				}
			}

			termini.push({
				nodeId: node.id,
				position: { x: buttonX, y: buttonY },
				nestingDepth: getNodeBranchNestingDepth(node.id, nodes, edges),
				branchInfo,
				node,
			});
		}

		return termini;
	}, [nodes, edges]);
}

/**
 * Finds the parent switch node ID by tracing back through the graph.
 *
 * This function traverses the graph backwards from a given node to find
 * the nearest switch node that controls the branch this node is in.
 */
function findParentSwitchId(nodes: Node<BaseNodeData>[], edges: Edge[], nodeId: string): string | null {
	const visited = new Set<string>();
	const queue = [nodeId];

	while (queue.length > 0) {
		const currentId = queue.shift();
		if (!currentId || visited.has(currentId)) {
			continue;
		}

		visited.add(currentId);

		// Find the node
		const currentNode = nodes.find(n => n.id === currentId);
		if (!currentNode) {
			continue;
		}

		// Check if this is a switch node
		const nodeData = currentNode.data;
		if ('stepType' in nodeData) {
			const stepType = nodeData.stepType as string;
			if (stepType === 'Switch') {
				return currentId;
			}
		}

		// Also check for switchStep type
		if (currentNode.type === 'switchStep') {
			return currentId;
		}

		// Find incoming edges and add their sources to the queue
		for (const edge of edges) {
			if (edge.target === currentId && !visited.has(edge.source)) {
				queue.push(edge.source);
			}
		}
	}

	return null;
}

/**
 * Traces backwards through the graph from a node to find the nearest ancestor
 * segment node. Returns branchInfo derived from that segment's data.
 *
 * This handles the case where a step node is deep inside a branch — only the
 * first child in a branch gets parentCaseInfo, so later steps need to inherit
 * branch context from their ancestor segment.
 */
function findParentSegmentInfo(
	nodes: Node<BaseNodeData>[],
	edges: Edge[],
	nodeId: string
): ChainTerminus['branchInfo'] | null {
	const visited = new Set<string>();
	const queue = [nodeId];

	while (queue.length > 0) {
		const currentId = queue.shift();
		if (!currentId || visited.has(currentId)) {
			continue;
		}

		visited.add(currentId);

		const currentNode = nodes.find(n => n.id === currentId);
		if (!currentNode) {
			continue;
		}

		// Check if this is a segment node
		if (currentNode.type === 'segment' && 'parentSwitchNodeId' in currentNode.data) {
			const segmentData = currentNode.data as unknown as {
				parentSwitchNodeId: string;
				segmentIndex: number;
				caseId?: string;
				globalSegmentOrdinal?: number;
			};
			const segmentNumber = segmentData.globalSegmentOrdinal || segmentData.segmentIndex + 1;
			return {
				parentSwitchId: segmentData.parentSwitchNodeId,
				caseIndex: segmentData.segmentIndex,
				caseName: `Segment #${segmentNumber}`,
				caseId: segmentData.caseId,
			};
		}

		if (currentNode.type === NODE_TYPES.BEHAVIOR_CASE && 'parentSwitchNodeId' in currentNode.data) {
			const behaviorCaseData = currentNode.data as unknown as {
				parentSwitchNodeId: string;
				caseIndex: number;
				caseName?: string;
				caseId?: string;
				globalSegmentOrdinal?: number;
			};
			const fallbackCaseName = behaviorCaseData.globalSegmentOrdinal
				? `Segment #${behaviorCaseData.globalSegmentOrdinal}`
				: `Case #${behaviorCaseData.caseIndex + 1}`;
			return {
				parentSwitchId: behaviorCaseData.parentSwitchNodeId,
				caseIndex: behaviorCaseData.caseIndex,
				caseName: behaviorCaseData.caseName || fallbackCaseName,
				caseId: behaviorCaseData.caseId,
			};
		}

		// Trace backwards through incoming edges
		for (const edge of edges) {
			if (edge.target === currentId && !visited.has(edge.source)) {
				queue.push(edge.source);
			}
		}
	}

	return null;
}

/**
 * Checks if a node is a valid target for adding steps.
 *
 * @param node - The node to check
 * @returns true if steps can be added after this node
 */
export function canAddStepAfterNode(node: Node<BaseNodeData>): boolean {
	// Can't add after error nodes
	if (node.type === 'error') {
		return false;
	}

	// Can add after any non-terminal step node
	const nodeData = node.data;
	if ('stepType' in nodeData) {
		return node.type !== NODE_TYPES.TRIGGER_AUTOMATION_STEP;
	}

	// Can add after trigger nodes
	if (node.type === 'trigger') {
		return true;
	}

	// Can add after condition nodes (branch endpoints)
	if (node.type === 'condition') {
		return true;
	}

	return false;
}
